/** external */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Dropdown, DropdownMenu } from "@toddle-edu/ds-web";
import {
  CaretDownFilled,
  ChevronDownCircleOutlined,
  ChevronUpCircleOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import _ from "lodash";

/** internal */

/** relative */
import classes from "./PromptAction.scss";
import { getPromptIcon } from "../../modules/Utils";
import { onEnterOrSpaceKeyDown } from "Utils";
import { useI18n } from "Hooks";

const PromptAction = props => {
  const { promptItems, onPromptActionClick, isActionsDisabled } = props;
  const { t } = useI18n();

  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    setIsDisabled(isActionsDisabled);
  }, [isActionsDisabled]);

  if (!_.size(promptItems)) return null;

  const handlePromptActionClick = action => {
    if (isDisabled) return;
    setIsDisabled(true);
    onPromptActionClick?.(action);
  };

  return (
    <div className={classes.promptActionContainer}>
      {_.map(promptItems, option => {
        const { id: optionId, label, componentType, options, prompts, action } =
          option || {};

        const icon = getPromptIcon({ key: action || optionId });

        const promptActionItemClasses = classNames(classes.promptActionItem, {
          [classes.disabledAction]: isDisabled,
        });

        switch (componentType) {
          case "increase_decrease_difficulty": {
            const itemClasses = classNames(
              classes.promptActionItem,
              classes.increaseDescreaseItem,
              {
                [classes.disabledAction]: isDisabled,
              }
            );

            const increaseDescreaseIconClasses = classNames(
              classes.increaseDescreaseIcon,
              {
                [classes.disabledAction]: isDisabled,
              }
            );

            const { increaseDifficulty, decreaseDifficulty } = prompts;

            return (
              <div className={itemClasses} key={optionId}>
                {label}
                <div className={classes.increaseDescreaseIconContainer}>
                  <div
                    className={increaseDescreaseIconClasses}
                    onClick={() => handlePromptActionClick(increaseDifficulty)}
                    onKeyDown={e => {
                      onEnterOrSpaceKeyDown(e, () =>
                        handlePromptActionClick(increaseDifficulty)
                      );
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={t("ai:increase_difficulty_of_the_question")}
                  >
                    <ChevronUpCircleOutlined
                      size="xxx-small"
                      className={classes.increaseIcon}
                    />
                  </div>
                  <div
                    className={increaseDescreaseIconClasses}
                    onClick={() => handlePromptActionClick(decreaseDifficulty)}
                    onKeyDown={e => {
                      onEnterOrSpaceKeyDown(e, () =>
                        handlePromptActionClick(decreaseDifficulty)
                      );
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={t("ai:decrease_difficulty_of_the_question")}
                  >
                    <ChevronDownCircleOutlined
                      size="xxx-small"
                      className={classes.decreaseIcon}
                    />
                  </div>
                </div>
              </div>
            );
          }

          case "dropdown":
            return (
              <Dropdown
                key={optionId}
                overlay={
                  <DropdownMenu
                    options={options}
                    onClick={({ key }) => {
                      const selectedOption = _.find(options, { key });
                      handlePromptActionClick(selectedOption);
                    }}
                  />
                }
                disabled={isDisabled}
              >
                <div
                  className={promptActionItemClasses}
                  tabIndex={0}
                  role="button"
                  aria-haspopup="listbox"
                  aria-expanded="false"
                >
                  {icon}
                  {label}
                  <CaretDownFilled variant="subtle" />
                </div>
              </Dropdown>
            );

          default:
            return (
              <div
                key={optionId}
                className={promptActionItemClasses}
                onClick={() => handlePromptActionClick(option)}
                onKeyDown={e => {
                  onEnterOrSpaceKeyDown(e, () =>
                    handlePromptActionClick(option)
                  );
                }}
                tabIndex={0}
                role="button"
              >
                {icon}
                {label}
              </div>
            );
        }
      })}
    </div>
  );
};

PromptAction.propTypes = {
  promptItems: PropTypes.arrayOf(PropTypes.object),
  onPromptActionClick: PropTypes.func,
  isActionsDisabled: PropTypes.bool,
};

PromptAction.defaultProps = {
  promptItems: [],
  onPromptActionClick: _.noop,
  isActionsDisabled: false,
};

PromptAction.displayName = "PromptAction";

export default PromptAction;
